import React, { useEffect, useRef, useState } from 'react';
import { NumericFormat, NumberFormatValues } from 'react-number-format';
import { Popup } from 'semantic-ui-react';

interface ICurrencyInputProps {
  value?: number | string;
  maxValue?: number;
  minValue?: number;
  currencySymbol?: string;
  setValue: (num: number) => void;
}

const convertToNumber = (num?: number | string) => {
  const value = Number(num);
  if (isNaN(value)) return 0;
  return value;
};

export const CurrencyInput = ({
  value,
  maxValue,
  minValue,
  currencySymbol,
  setValue
}: ICurrencyInputProps) => {
  const inputRef = useRef(null);
  const num = convertToNumber(value);
  const isError = minValue && num < minValue;
  const [displayValue, setDisplayValue] = useState(num?.toFixed(2));

  // Read the committed amount from the numeric value NumericFormat already
  // parsed, rather than re-parsing the formatted string. The old approach
  // dropped a single leading character to skip the prefix, which only held
  // while the prefix was a hardcoded "$". Symbols are now caller supplied and
  // CurrencyHelper.getCurrencySymbol returns multi-character values for
  // several currencies (CHF, kr, R$, Rp, L£) plus the three letter code
  // whenever Intl has no narrow symbol, so "CHF50.00" parsed as NaN.
  const committedValue = useRef(num);

  const onChange = (data: NumberFormatValues) => {
    const floatValue = data.floatValue || 0;
    let isAllowed = true;
    if (maxValue) {
      isAllowed = floatValue <= maxValue;
    }
    return isAllowed;
  };

  useEffect(() => {
    setDisplayValue(num?.toFixed(2));
    committedValue.current = num;
  }, [value]);

  const component = (
    <div className={`ui input${isError ? ' error' : ''}`}>
      <NumericFormat
        value={displayValue}
        onValueChange={(values: NumberFormatValues) => {
          committedValue.current = values.floatValue ?? 0;
        }}
        onBlur={() => setValue(committedValue.current)}
        prefix={currencySymbol ?? '$'}
        displayType="input"
        type="text"
        isAllowed={onChange}
        thousandSeparator
        allowNegative={false}
        decimalScale={2}
      />
    </div>
  );

  if (isError) {
    return (
      <Popup
        content={`Value must be greater than ${currencySymbol ?? '$'}${minValue?.toFixed(2)}`}
        trigger={component}
      />
    );
  }
  return component;
};
